"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClassSubjectDef = void 0;
const error_1 = require("../error");
const subject_instance_1 = require("./subject-instance");
const SUBJECT_DEF = Symbol('ecce-subject-def');
/**
 * Defines a class subject.
 *
 * This is attached to the prototype of classes decorated with the `@subject`
 * decorator. As individual decorators are invoked, they add their details to
 * this class.
 */
class ClassSubjectDef {
    /**
     * Called from {@link ClassSubjectDef.getOrCreate()};
     */
    constructor(name) {
        /** Defs of properties which were decorated with `@observable`. */
        this.properties = new Map();
        /** Keys of methods which were decorated with `@callback`. */
        this.callbacks = new Set();
        /**
         * This is set to `true` in the `@subject` decorator.
         * @see {@link ClassSubjectDef.assertWasDecorated}.
         */
        this.wasDecorated = false;
        this.name = name;
        this.assertWasDecorated();
    }
    /**
     * Add an observable property.
     *
     * Called from the `@observable()` decorator.
     */
    addProperty(key, descriptor) {
        this.properties.set(key, { key, descriptor });
    }
    /**
     * Add a callback method.
     *
     * Called from the `@callback()` decorator.
     */
    addCallback(key) {
        this.callbacks.add(key);
    }
    apply(instance) {
        subject_instance_1.SubjectInstance.apply(instance, this.name, this.properties.values());
        for (const callback of this.callbacks) {
            const method = instance[callback];
            if (typeof (method) !== 'function') {
                throw new error_1.EcceError(`Cannot apply @callback to non-function member: "${this.name}.${String(callback)}".`);
            }
            instance[callback] = method.bind(instance);
        }
    }
    /**
     * Assert that the class this represents was decorated with `@subject()`.
     *
     * We want to emit a clear error message if `@observable()` or `@callback` are
     * applied to members of a class without a `@subject()` decorator.
     *
     * However, as property & method decorators are invoked _before_ class
     * decorators, we have no way of knowing from inside those whether the
     * class has the `@subject()` decorator or not.
     *
     * So, we set a timeout for next tick to check if `wasDecorated` is true, and
     * set that to true in `@subject()`.
     *
     * This is a pretty ugly way of handling this -- ideally we would be able to
     * throw this on construction of an invalid instance, but we will not have
     * been able to proxy the class' constructor without the @subject()
     * decorator...
    */
    assertWasDecorated() {
        setTimeout(() => {
            if (!this.wasDecorated) {
                throw new error_1.EcceError(`Cannot use @observable() on a member of a class not decorated with @subject(): "${this.name}"`);
            }
        });
    }
    /**
     * Called from `@subject()` decorator to verify that the class this represents
     * was actually decorated with `@subject()`.
     *
     * @see {@link ClassSubjectDef.assertWasDecorated}.
     */
    decorated() {
        this.wasDecorated = true;
    }
    /**
     * Get the `ClassSubjectDef` attached to `prototype`, or create a new one if
     * it does not exist.
     */
    static getOrCreate(prototype) {
        if (!(SUBJECT_DEF in prototype)) {
            return prototype[SUBJECT_DEF] = new ClassSubjectDef(prototype.constructor.name);
        }
        return prototype[SUBJECT_DEF];
    }
}
exports.ClassSubjectDef = ClassSubjectDef;
//# sourceMappingURL=class-subject-def.js.map